import { Tabs as UnstyledTabs, alpha, styled } from "@mui/material";

import { Header as UnstyledHeader } from "@citrine/atoms/header";
import { Select as UnstyledSelect } from "@citrine/atoms/select";
import type { ColorName } from "@citrine/theme";
import { TAB_INDICATOR_SPACING } from "@citrine/theme";
import { DEFAULT_SKEW, getHorizontalOffset } from "@citrine/utils/skew";

import { Row } from "../layout";

import { NavTab as UnstyledNavTab } from "./nav-tab";

export type Size = "large" | "small";
export type Variant = "buttons" | "select";

interface WrapperProps {
  size: Size;
  variant: Variant;
}

export const Wrapper = styled("div", {
  shouldForwardProp: (propName: keyof WrapperProps) =>
    propName !== "size" && propName !== "variant",
})<WrapperProps>(({ theme, size, variant }) => [
  variant === "select"
    ? {
        border: `1px solid ${theme.palette.grey[300]}`,
        marginTop: theme.spacing(3),
        padding: theme.spacing(3.5, 1.5, 3),
        position: "relative",
      }
    : null,

  size === "small"
    ? {
        border: `1px solid ${theme.palette.grey[300]}`,
        padding: theme.spacing(3),
        position: "relative",
      }
    : null,
]);

export const HeaderContainer = styled(Row)({
  justifyContent: "space-between",
});

export const Header = styled(UnstyledHeader)(({ theme }) => ({
  gap: theme.spacing(2),
}));

interface ScrollableProps {
  size: Size;
}

export const Scrollable = styled("div", {
  shouldForwardProp: (propName: keyof ScrollableProps) => propName !== "size",
})<ScrollableProps>(({ theme, size }) => [
  {
    // flex: 1 is used to not clobber children appended to the end of the row
    flex: 1,
    maxWidth: `calc(100% - ${theme.spacing(6)})`,
    // this width is necessary for auto-toggling scrolling when page is resized
    width: "100%",
  },

  size === "small"
    ? {
        position: "absolute",
        top: 0,
        transform: "translateY(-50%)",
      }
    : null,
]);

export const Select = styled(UnstyledSelect)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  top: 0,
  transform: "translateY(-50%)",

  "& .MuiSelect-root": {
    height: theme.spacing(4),
    padding: theme.spacing(0, 3, 0, 1),
  },

  "& .MuiSelect-icon": {
    right: theme.spacing(0.625),
  },
}));

export const TabLabel = styled(Row)(({ theme }) => ({
  gap: theme.spacing(1),
}));

interface NavTabProps {
  color?: ColorName;
  size?: Size;
  skew?: boolean;
  spacing?: number;
}

export const NavTab = styled(UnstyledNavTab, {
  shouldForwardProp: (propName: keyof NavTabProps) =>
    !["color", "size", "skew", "spacing"].includes(propName),
})<NavTabProps>(({ theme, color = "primary", size, skew, spacing }) =>
  size === "small"
    ? {
        // Put background color on individual tab labels in order to avoid
        // background color issues related to scrollable tabs
        background: theme.palette.grey[50],
        border: `solid 1px ${theme.palette.grey[300]}`,
        color: theme.palette.grey[600],
        fontSize: theme.spacing(1.5),
        fontWeight: 500,
        height: theme.spacing(4),
        letterSpacing: "0.75px",
        lineHeight: theme.spacing(2),
        margin: 0,
        minHeight: theme.spacing(4),
        padding: 0,
        transform: skew ? `skew(${-DEFAULT_SKEW}rad)` : "",
        transformOrigin: "bottom",
        "&.Mui-selected": {
          background: theme.palette[color].main,
          color: theme.palette.common.white,
          fontWeight: 700,
        },
        "&.visited": {
          color: theme.palette.grey[900],
          fontWeight: 600,
          opacity: 1,
        },
        "&.Mui-disabled&[role=tab]": {
          // Can't use opacity to lighten background/border/font colors
          // because it'll expose the tab panel border underneathe - instead
          // manually adjust the background, border, and font colors.
          background: theme.palette.grey[50],
          border: `solid 1px ${alpha(theme.palette.grey[300], 0.4)}`,
          color: alpha(theme.palette.grey[500], 0.4),
          fontWeight: 500,
          opacity: 1,
        },
        "&::after": {
          display: "none",
        },
      }
    : {
        color: theme.palette.grey[600],
        fontSize: theme.spacing(1.75),
        fontWeight: 500,
        letterSpacing: "1px",
        margin:
          spacing === undefined
            ? ""
            : theme.spacing(0, spacing, TAB_INDICATOR_SPACING, 0),
        "&.Mui-selected": {
          color: theme.palette.grey[900],
          fontWeight: 900,
        },
        "&.visited": {
          opacity: 1,
          fontWeight: 500,
          color: theme.palette.grey[900],
          "&::after": {
            backgroundColor: theme.palette.grey[700],
          },
        },
        "&.Mui-disabled": {
          fontWeight: 500,
          color: theme.palette.grey[400],
        },
        // using a ternary to determine "&::after" transform causes warnings
        // to be thrown in jest tests
        ...(skew
          ? {
              "&::after": {
                transform: `skew(${-DEFAULT_SKEW}rad)`,
              },
            }
          : {}),
      },
);

interface TabsProps {
  color: ColorName;
  hasHeader?: boolean;
  selection?: string | false;
  size?: Size;
  skew?: boolean;
}

export const Tabs = styled(UnstyledTabs, {
  shouldForwardProp: (propName: keyof TabsProps) =>
    !["color", "hasHeader", "selection", "size", "skew"].includes(propName),
})<TabsProps>(({ theme, color, hasHeader, selection, size, skew }) => [
  size === "small"
    ? {
        marginBottom: 0, // there is no indicator arrow, so remove the excess margin
        minHeight: theme.spacing(4),
        height: theme.spacing(4),
        overflow: "visible",
      }
    : {},

  {
    "& .MuiTabs-flexContainer": {
      // when a header is present in large mode, right-align the tabs
      justifyContent: hasHeader && size === "large" ? "flex-end" : "flex-start",
    },

    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette[color].main,
      transform: skew ? `skew(${-DEFAULT_SKEW}rad)` : undefined,
      display: size === "small" || !selection ? "none" : "block",
      "&::after": {
        borderTopColor: theme.palette[color].main,
        /**
         * this transform reverts the skew for the indicator's notch
         * it also centers the notch, which is placed at left: 50%
         * but that still leaves it pushed to the right of center
         * with no skew, translateX(-50%) addresses this centering issue
         * but with skew applied, we need to translate for both the height of the notch
         * and the half-height of the indicator (hence, 1.5)
         */
        transform: skew
          ? `skew(${DEFAULT_SKEW}rad) translateX(${theme.spacing(
              TAB_INDICATOR_SPACING * (getHorizontalOffset(1.5) - 1),
            )})`
          : undefined,
        display: size === "small" ? "none" : undefined,
      },
    },

    "& .MuiTabs-scrollButtons": {
      alignItems: "center",
      // adjust for the indicator arrow
      marginBottom: size === "large" ? theme.spacing(0.75) : 0,
      transition: theme.transitions.create("opacity"),
      width: size === "small" ? theme.spacing(4) : theme.spacing(5),

      "&:first-of-type": {
        marginLeft: size === "small" ? theme.spacing(-2) : undefined,
      },

      "&:last-of-type": {
        marginRight: size === "small" ? theme.spacing(-2) : undefined,
      },

      "&.Mui-disabled": {
        opacity: 0.3,
      },
    },

    /**
     * the indicator skew is anchored at its vertical center (hence, the 0.5),
     * leading to the bottom-left corner extending beyond the tab-panel's left edge
     * this padding translates the indicators right so their bottom-left corner
     * aligns properly with the left edge of the tab-panel
     */
    "& .MuiTabs-scroller": {
      margin: 0,
      overflowY: "clip",
      padding: 0,
      // add left padding for the skewed indicator's edge; 3px is half-height of the 6px indicator
      paddingLeft:
        size === "large" && skew
          ? theme.spacing(getHorizontalOffset(0.375))
          : undefined,
    },
  },
]);
